import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { type PointerEvent as ReactPointerEvent, useId, useState } from 'react';

import { VisuallyHidden } from './VisuallyHidden';

type Props = {
  /** What is being explained, e.g. "the value URA will tax". Names the button. */
  label: string;
  /** The explanation. One or two short sentences of plain language. */
  children: string;
};

/**
 * One sentence of plain English, parked beside the thing it explains.
 *
 * ## Why not a hover tooltip
 *
 * Because most of this site's visitors are holding a phone, and a hover
 * tooltip does not exist for them. The rest of the site can live with that —
 * a client logo nobody hovers is still a logo — but the calculators cannot:
 * what "CIF" means or why URA ignores the price you paid is not decoration,
 * it is the difference between a figure a visitor trusts and a figure they
 * misread. So this opens three ways and is the same control each time: tap,
 * hover, or Tab and press Enter.
 *
 * ## Why `pointerType` and not a media query
 *
 * A tap synthesises `mouseover` before `click`, so a hover listener and a
 * click listener on the same element cancel each other out on a phone — the
 * hint opens on the mouse event and closes again on the tap. Asking the event
 * which device sent it settles that per interaction, with no `matchMedia` call
 * and nothing for the prerendered HTML to disagree with (see `POINTER_HOVER`
 * in the tokens for why that matters here).
 *
 * The keyboard path is gated on `:focus-visible` rather than focus, because a
 * mouse click focuses the button too and would leave the hint open under the
 * cursor that just dismissed it.
 *
 * ## Why the words are also hidden text
 *
 * A tooltip is announced only once it is open, and a description that arrives
 * after focus is not reliably read. So the explanation is always in the
 * accessible tree as the button's description, whether or not anything is on
 * screen — the visible tooltip is for eyes, `aria-describedby` is the contract.
 */
export function InfoHint({ label, children }: Props) {
  const [open, setOpen] = useState(false);
  const descriptionId = useId();

  /* Only a real cursor should open this on approach; a tap must fall through
     to the click handler below, which is what opens it on a phone. */
  const onHover = (next: boolean) => (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse') setOpen(next);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
        <Tooltip
          open={open}
          /* Controlled, so MUI's own triggers would only fight the handlers
             below — but `onClose` is kept because it is also how Tooltip
             reports Escape, which is the one dismissal we do not own. */
          onClose={() => setOpen(false)}
          disableHoverListener
          disableFocusListener
          disableTouchListener
          describeChild
          title={children}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 272,
                p: 3,
                /* Inverted from the two tokens rather than MUI's fixed grey,
                   so the bubble is dark on the light scheme and light on the
                   dark one without a second colour being declared anywhere. */
                bgcolor: 'text.primary',
                color: 'background.paper',
                fontSize: '0.8125rem',
                fontWeight: 400,
                lineHeight: 1.55,
              },
            },
            arrow: { sx: { color: 'text.primary' } },
          }}
        >
          <IconButton
            type="button"
            aria-label={`About ${label}`}
            aria-describedby={descriptionId}
            aria-expanded={open}
            onClick={() => setOpen((previous) => !previous)}
            onPointerEnter={onHover(true)}
            onPointerLeave={onHover(false)}
            onFocus={(event) => {
              if (event.currentTarget.matches(':focus-visible')) setOpen(true);
            }}
            onBlur={() => setOpen(false)}
            sx={{
              /* 28px clears the 24px minimum target of WCAG 2.5.8 while still
                 sitting on a caption's line without pushing it apart. */
              width: 28,
              height: 28,
              p: 0,
              ml: 1,
              color: open ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HelpOutlineRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <VisuallyHidden id={descriptionId}>{children}</VisuallyHidden>
      </Box>
    </ClickAwayListener>
  );
}
